import { supabase } from './supabase';

export type CapturedIdeaSource = 'whatsapp' | 'handmatig' | 'ai_suggestie';
export type CapturedIdeaStatus = 'nieuw' | 'in_bewerking';

export interface CapturedIdea {
  id: string;
  user_id: string;
  source: CapturedIdeaSource;
  title: string;
  content: string;
  status: CapturedIdeaStatus;
  session_id: string | null;
  created_at: string;
  updated_at: string;
}

// Open ideeën: nieuw + in_bewerking, exclusief ideeën waarvan de gekoppelde
// sessie afgerond ('completed') of verlaten ('abandoned') is.
export async function listOpenIdeas(userId: string): Promise<CapturedIdea[]> {
  const { data, error } = await supabase
    .from('captured_ideas')
    .select('*, sessions(state)')
    .eq('user_id', userId)
    .in('status', ['nieuw', 'in_bewerking'])
    .order('status', { ascending: true })   // in_bewerking vóór nieuw (i < n)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).filter((row: any) => {
    const st = row.sessions?.state;
    return !st || (st !== 'completed' && st !== 'abandoned');
  }) as CapturedIdea[];
}

export async function countNewIdeas(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('captured_ideas')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('status', 'nieuw');
  if (error) { console.error('countNewIdeas failed', error); return 0; }
  return count ?? 0;
}

export async function createIdea(
  userId: string,
  source: CapturedIdeaSource,
  title: string,
  content: string,
): Promise<CapturedIdea> {
  const { data, error } = await supabase
    .from('captured_ideas')
    .insert({ user_id: userId, source, title, content, status: 'nieuw' })
    .select('*')
    .single();
  if (error) throw error;
  return data as CapturedIdea;
}

export async function updateIdea(id: string, title: string, content: string): Promise<void> {
  const { error } = await supabase
    .from('captured_ideas')
    .update({ title, content, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function deleteIdea(id: string): Promise<void> {
  const { error } = await supabase.from('captured_ideas').delete().eq('id', id);
  if (error) throw error;
}

// "Werk uit": maak sessie (state idea_approved), seed blog_idea, koppel idee.
// Returnt session_id voor navigatie naar /article-direction.
// Zet een aan deze (verlaten) sessie gekoppeld idee terug in de inbox, zodat het
// weer in /ideeen verschijnt (listOpenIdeas verbergt ideeën van abandoned sessies).
export async function returnIdeaToInboxBySession(sessionId: string): Promise<void> {
  const { error } = await supabase
    .from('captured_ideas')
    .update({ status: 'nieuw', session_id: null, updated_at: new Date().toISOString() })
    .eq('session_id', sessionId)
    .eq('status', 'in_bewerking');
  if (error) throw error;
}

export async function startWizardFromIdea(idea: CapturedIdea): Promise<string> {
  const { data: session, error: sErr } = await supabase
    .from('sessions')
    .insert({ user_id: idea.user_id, state: 'idea_approved' })
    .select('id')
    .single();
  if (sErr || !session) throw sErr ?? new Error('Sessie aanmaken mislukt');

  const ideaSource = idea.source === 'ai_suggestie' ? 'ai_regenerated' : 'user_custom';
  const { data: blogIdea, error: biErr } = await supabase
    .from('blog_ideas')
    .insert({
      session_id: session.id,
      user_id: idea.user_id,
      source: ideaSource,
      idea_title: idea.title,
      idea_content: idea.content,
      status: 'approved',
    })
    .select('id')
    .single();
  if (biErr || !blogIdea) throw biErr ?? new Error('Idee seeden mislukt');

  const { error: updErr } = await supabase
    .from('sessions').update({ approved_idea_id: blogIdea.id }).eq('id', session.id);
  if (updErr) throw updErr;

  const { error: capErr } = await supabase
    .from('captured_ideas')
    .update({ status: 'in_bewerking', session_id: session.id, updated_at: new Date().toISOString() })
    .eq('id', idea.id);
  if (capErr) throw capErr;

  return session.id;
}
